import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import type { OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import type {
  AfterViewChecked,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  Output,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { shakeAnimation } from '@freelancer/animations';
import { RepetitiveSubscription } from '@freelancer/decorators';
import type { Timer } from '@freelancer/time-utils';
import { TimeUtils } from '@freelancer/time-utils';
import { IconColor, IconModule, IconSize } from '@freelancer/ui/icon';
import { ScrollableContentComponent } from '@freelancer/ui/scrollable-content';
import type { SelectItem } from '@freelancer/ui/select';
import { IconConfig, TagType } from '@freelancer/ui/tag';
import { TagComponent } from '@freelancer/ui/tag/tag.component';
import { FontColor } from '@freelancer/ui/text';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import {
  dirtyAndValidate,
  validContactNumberRegex,
} from '@freelancer/ui/validators';
import { isEqual, parseEmailCSVValues } from '@freelancer/utils';
import { Subscription } from 'rxjs';
import { map, pairwise, startWith, throttleTime } from 'rxjs/operators';
import { Focus } from '../focus/focus.service';
import { textVariants } from '../text';
import {
  MULTISELECT_THROTTLE_TIME,
  MultiSelectCounterPosition,
  type MultiSelectInputBlurBehaviour,
  MultiSelectItemHighlightColor,
  MultiSelectSize,
} from './multi-select.types';

@Component({
  standalone: true,
  imports: [
    IconModule,
    NgStyle,
    NgTemplateOutlet,
    ReactiveFormsModule,
    ScrollableContentComponent,
    TagComponent,
    ValidationErrorComponent,
  ],
  selector: 'fl-multi-select',
  template: `
    <div
      class="MultiSelectContainer"
      role="combobox"
      [attr.aria-describedby]="errorDescribedBy"
      [attr.aria-expanded]="dropdownVisible"
      [attr.aria-haspopup]="'listbox'"
      [attr.aria-owns]="dropdownContainerId"
    >
      <div
        class="MultiSelectInner {{
          maxSelections !== undefined || hasError ? 'mb-xxxsmall' : 'mb-none'
        }}"
      >
        <div
          #inputContainer
          class="InputContainer"
          [class.Disabled]="control.disabled"
          [class.Error]="hasError && !isWarning"
          [class.HasSelections]="selectedItems.length > 0"
          [class.IsFocused]="isFocused"
          [ngStyle]="{ maxHeight: maxHeight }"
          [@shakeAnimation]="{
            value: shakeState,
            params: {
              duration: 200,
              translateX: '3px',
            },
          }"
          (@shakeAnimation.done)="shakeDone()"
        >
          @for (selection of selectedItems; track $index) {
            <fl-tag
              class="mr-xxsmall"
              role="option"
              aria-selected="true"
              [type]="TagType.DISMISSABLE"
              [iconConfig]="selection.iconConfig"
              [isInvalid]="selection.isInvalid"
              (mousedown)="handleMousedown($event)"
              (dismiss)="removeItem($index)"
              (dblclick)="modifyItem($index)"
              (touchstart)="handleTouchStart($index)"
              (touchend)="handleTouchEnd()"
            >
              {{ selection.displayText }}
            </fl-tag>
          }
          <!-- eslint-disable local-rules/template-attribute-names -->
          <input
            #rawInput
            class="RawInput"
            autocomplete="off"
            data-1p-ignore
            data-lpignore="true"
            [ngStyle]="{ width: inputWidth + 'ch' }"
            [attr.spellcheck]="spellcheck"
            [attr.aria-controls]="dropdownContainerId"
            [attr.aria-invalid]="
              (control.touched || control.dirty) && control.invalid
            "
            [attr.maxlength]="allowCustomEntry && maxLength ? maxLength : null"
            [class.Hide]="
              (selectedItems.length > 0 && hideInputOnNonEmpty) || disableInput
            "
            [formControl]="inputControl"
            (keyup)="handleInputKeyUp($event)"
            (keydown)="handleInputKeyDown($event)"
            (focus)="handleInputFocus()"
            (blur)="handleInputBlur()"
            placeholder="{{
              placeholder && (!control.value || control.value?.length === 0)
                ? placeholder
                : secondaryPlaceholder
            }}"
          />
          <!-- eslint-enable local-rules/template-attribute-names -->
        </div>
        @if (!dropdownOverlay && dropdownVisible && !disableDropdown) {
          <ng-container *ngTemplateOutlet="dropdownPanel"></ng-container>
        }
      </div>
      <div class="Footer">
        @if (
          maxSelections !== undefined &&
          (hasError
            ? counterPosition === MultiSelectCounterPosition.RIGHT
            : true)
        ) {
          <p
            i18n="Counter for selected items"
            [class]="counterTextCls"
            [class.Footer-leftSide]="
              counterPosition === MultiSelectCounterPosition.LEFT
            "
            [class.Footer-rightSide]="
              counterPosition === MultiSelectCounterPosition.RIGHT
            "
            [class.hide]="
              counterPosition === MultiSelectCounterPosition.LEFT && hasError
            "
          >
            {{ selectionsCount }}/{{ maxSelections }} selected
          </p>
        }
        @if (hasError || counterPosition === MultiSelectCounterPosition.RIGHT) {
          <fl-validation-error
            class="Footer-leftside"
            [id]="errorId"
            [control]="control"
            [isWarning]="isWarning"
            [showMultipleErrors]="showMultipleErrors"
          ></fl-validation-error>
        }
      </div>
    </div>

    <ng-template #dropdownPanel>
      <div
        class="DropdownContainer"
        role="listbox"
        [attr.id]="dropdownContainerId"
        [class.IsOverlay]="dropdownOverlay"
      >
        <fl-scrollable-content
          class="DropdownScrollbar"
          [autoHeight]="true"
          [hideHorizontalScroll]="true"
        >
          @for (
            option of filteredOptions;
            track trackBySelectItem(i, option);
            let i = $index;
            let first = $first
          ) {
            <div
              class="DropdownItem"
              (click)="handleDropdownClick(option)"
              (mousedown)="handleMousedown($event)"
              [class.DarkBlueHighlight]="
                dropdownItemSelectColor ===
                MultiSelectItemHighlightColor.BLUE_DARK
              "
              [class.GrayHighlight]="
                dropdownItemSelectColor === MultiSelectItemHighlightColor.GRAY
              "
              [class.IsDisabled]="option.disabled"
              [class.IsHighlighted]="
                i === highlightIndex && !option.disabled && showHighlight
              "
            >
              <p
                id="{{ option.displayText }}"
                role="option"
                [class]="
                  i === highlightIndex && !option.disabled && showHighlight
                    ? dropdownItemTextClasses[i]?.highlighted
                    : dropdownItemTextClasses[i]?.normal
                "
              >
                {{ option.displayText }}
              </p>
              @if (first && showAddingLine && showPlusIcon) {
                <fl-icon
                  class="mr-xxsmall"
                  [color]="IconColor.SECONDARY"
                  [name]="'ui-add-circle'"
                  [size]="IconSize.MID"
                  [useIconFont]="true"
                ></fl-icon>
              }
            </div>
          }
        </fl-scrollable-content>
      </div>
    </ng-template>
  `,
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [shakeAnimation],
})
export class MultiSelectComponent<T>
  implements AfterViewChecked, OnChanges, OnDestroy, OnInit
{
  private timeUtils = inject(TimeUtils);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private focus = inject(Focus);
  private ngZone = inject(NgZone);
  private overlay = inject(Overlay);
  private renderer = inject(Renderer2);
  private viewContainerRef = inject(ViewContainerRef);

  IconColor = IconColor;
  IconSize = IconSize;
  MultiSelectItemHighlightColor = MultiSelectItemHighlightColor;
  MultiSelectCounterPosition = MultiSelectCounterPosition;
  TagType = TagType;

  dropdownContainerId: string;
  errorId: string;

  @HostBinding('class.IsFocused') isFocused = false;
  private _dropdownVisible = false;

  get dropdownVisible(): boolean {
    return this._dropdownVisible;
  }

  private set dropdownVisible(value: boolean) {
    this._dropdownVisible = value;
    if (this.dropdownOverlay) {
      if (value && !this.disableDropdown) {
        this.attachDropdownOverlay();
      } else {
        this.detachDropdownOverlay();
      }
    }
  }
  filteredOptions: SelectItem<string | T>[] = [];
  dropdownItemTextClasses: readonly {
    readonly normal: string;
    readonly highlighted: string;
  }[] = [];
  highlightIndex = 0;
  inputContainerHeight = 46;
  inputWidth = 1;
  inputControl = new FormControl('', { nonNullable: true });
  isWarning = false;
  maxHeight: string;
  selectedItems: SelectItem<string | T>[] = [];
  /** Indicates whether we are displaying the "Adding" line in the dropdown */
  showAddingLine = false;
  showHighlight: boolean;
  shakeState = 'void';
  selectionsCount = 0;

  private longPressTimeout: Timer;

  private documentScrollEvent?: () => void;
  @RepetitiveSubscription()
  private errorSubscription?: Subscription;
  private inputSubscription?: Subscription;
  private valueChangedSubscription?: Subscription;

  @ViewChild('rawInput') rawInput: ElementRef<HTMLInputElement>;
  @ViewChild('inputContainer', { read: ElementRef })
  inputContainer: ElementRef<HTMLDivElement>;
  @ViewChild('dropdownPanel', { static: true })
  private dropdownPanelTemplate!: TemplateRef<void>;

  /**
   * Allow custom items to be added by the user.
   *
   * Note: `control` must accept `string` type if set to `true`.
   */
  @Input() allowCustomEntry?: T extends string ? boolean : false;
  @Input() control: FormControl<readonly T[]>;

  /**
   * Customize the appearance of the adding line in the dropdown.
   * When enabled, an add icon and the specified text will appear in blue.
   */
  @Input() showPlusIcon = false;

  /**
   * Customize the text displayed for adding options.
   * The default is 'Adding 'text''. When set, it will be displayed as 'displayText 'text''.
   */
  @Input() addingText = $localize`Adding`;

  /**
   * Disable the dropdown from showing.
   * The default is false. When set to true, the dropdown will not show.
   * This option is useful if using a different design to select the options.
   */
  @Input() disableDropdown = false;
  /** Render the dropdown in a CDK overlay so modal overflow:hidden cannot clip it. */
  @Input() dropdownOverlay = false;

  /**
   * Disable the input element from showing.
   * The default is false. When set to true, the input element will not show.
   * This option is useful for preventing the user from inputting while a load is in progress.
   */
  @Input() disableInput = false;

  /**
   * Initially show the highlight effect in the dropdown.
   * If set to false, users can trigger the highlight by navigating using the 'up' or 'down' buttons.
   */
  @Input() showHighlightInitially = true;

  /** Customize the color of a dropdown item when it is selected. */
  @Input() dropdownItemSelectColor: MultiSelectItemHighlightColor =
    MultiSelectItemHighlightColor.BLUE_DARK;

  /**
   * Conditionally hide the input element when there are selected items.
   * If there are already selected items, the input element will be hidden.
   */
  @Input() hideInputOnNonEmpty = false;
  @Input() invalidInputs: readonly string[] = [];
  /** Set to false for emails, usernames and other identifiers. */
  @Input() spellcheck?: boolean;
  @Input() openDropdownOnFocus = false;
  @Input() options: readonly SelectItem<T>[] = [];
  @Input() size = MultiSelectSize.MID;
  @Input() textColor: FontColor = FontColor.FOREGROUND;
  @Input() maxSelections?: number;
  @Input() counterPosition?: MultiSelectCounterPosition =
    MultiSelectCounterPosition.LEFT;

  counterTextCls: string;

  @Input()
  set maxRows(value: number) {
    if (value) {
      this.maxHeight = `${value * this.inputContainerHeight}px`;
    }
  }

  /** default placeholder when there's no entry yet */
  @Input() placeholder?: string;

  /** placeholder when there's already an entry */
  @Input() secondaryPlaceholder = '';

  /**
   * Number of characters you can type in the input.
   * This will only work when used with allowCustomEntry
   */
  @Input() maxLength?: number;

  /** Return false to block what the user typed from being added. Use with `allowCustomEntry`. */
  @Input() isCustomEntryValid?: (value: string) => boolean;

  /** First validation happens only on blur, and validates on keyup once an error exists */
  @Input() dynamicValidation?: boolean = false;

  /**
   * Allow pasting a string separated by `bulkEntrySeparators` to add multiple
   * emails at once. Bulk entry by username is not supported
   */
  @Input() allowBulkEntry = false;

  /**
   * Separators used to parse the string pasted into the input.
   * Use with `allowBulkEntry`.
   */
  @Input() bulkEntrySeparators: readonly string[] = [','];

  /**
   * What the input should do when blurred.
   * - 'clear' empties the field (default)
   * - 'add' adds the current value as a multiselect item
   * - 'none' leaves the text
   */
  @Input() blurBehaviour: MultiSelectInputBlurBehaviour = 'clear';
  /** Whether to show contact icons (eg. user, email) next to the items. */
  @Input() showContactIcons = false;
  @Input() showMultipleErrors = false;

  @Output() inputChange = new EventEmitter<string>();

  /**
   * This event carries the item that was removed.
   */
  @Output() itemRemoved = new EventEmitter<T | string>();

  @Output() onFocus = new EventEmitter<void>();
  @Output() onBlur = new EventEmitter<void>();

  get hasError(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }

  get errorDescribedBy(): string {
    const count = this.showMultipleErrors
      ? Object.keys(this.control.errors ?? {}).length
      : 1;
    // In the case of multiple errors, we need to return a space separated list of ids.
    // In the validation-error component, we generate these ids by appending the index to the base errorId.
    const extra = Array.from(
      { length: Math.max(count - 1, 0) },
      (_, i) => `${this.errorId}-${i + 1}`,
    );
    return [this.errorId, ...extra].join(' ');
  }

  private dropdownHideTimeout?: Timer;
  private dropdownOverlayRef?: OverlayRef;

  ngAfterViewChecked(): void {
    if (this.dropdownOverlayRef?.hasAttached()) {
      this.dropdownOverlayRef.updatePosition();
    }
  }

  ngOnInit(): void {
    this.showHighlight = this.showHighlightInitially;
    this.dropdownContainerId = `dropdown_${this.generateUniqueID()}`;
    this.errorId = `error_${this.generateUniqueID()}`;

    this.computeCounterTextClass();

    this.inputSubscription = this.inputControl.valueChanges
      // Throttle (not debounce) so rapid-fire changes (e.g. holding backspace) collapse without delaying isolated keystrokes.
      .pipe(
        throttleTime(MULTISELECT_THROTTLE_TIME, undefined, {
          leading: true,
          trailing: true,
        }),
      )
      .subscribe(value => {
        this.refilterOptions();
        this.fixupHighlightOverflow();
        this.inputChange.emit(value);
        this.changeDetectorRef.markForCheck();
      });
    // Set up disabled map to actual input field
    this.control.registerOnDisabledChange(disabled => {
      if (disabled) {
        this.inputControl.disable();
      } else {
        this.inputControl.enable();
      }
    });
    // Bind the invalid control to the class var
    if (this.control.disabled) {
      this.inputControl.disable();
    }

    this.valueChangedSubscription = this.control.valueChanges
      .pipe(
        startWith(this.control.value ?? []),
        pairwise(),
        map(([prev, curr]) => {
          const prevValues = prev ?? [];
          const currValues = curr ?? [];
          return {
            // Determine if the change was an addition or removal of an item
            type:
              prevValues.length === currValues.length
                ? 'none'
                : prevValues.length < currValues.length
                  ? 'addition'
                  : 'removal',
            previousValue: prev,
            currentValue: curr,
          };
        }),
      )
      .subscribe(change => {
        if (change.type === 'addition' && this.blurBehaviour === 'none') {
          // If the blurBehaviour is set to 'none', we want to clear the input field
          // when a new item is added
          this.rawInput.nativeElement.value = '';
        }
        this.getSelectedItems();
      });
  }

  private attachDropdownOverlay(): void {
    if (this.dropdownOverlayRef?.hasAttached()) {
      return;
    }
    this.dropdownOverlayRef ??= this.createDropdownOverlay();
    this.dropdownOverlayRef.updateSize({
      width: this.inputContainer.nativeElement.offsetWidth,
    });
    this.dropdownOverlayRef.attach(
      new TemplatePortal(this.dropdownPanelTemplate, this.viewContainerRef),
    );
    // Capture-phase document listener catches scroll on any container, including fl-modal's.
    this.ngZone.runOutsideAngular(() => {
      this.documentScrollEvent = this.renderer.listen(
        'document',
        'scroll',
        () => {
          this.dropdownOverlayRef?.updatePosition();
        },
        { capture: true, passive: true },
      );
    });
  }

  private createDropdownOverlay(): OverlayRef {
    // withPush(false) keeps the panel below the field; CDK's default slides it up when the panel is tall.
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.inputContainer)
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
      ]);

    return this.overlay.create(
      new OverlayConfig({
        positionStrategy,
        // noop: repositioning on scroll is handled by the Renderer2 document listener in attachDropdownOverlay.
        scrollStrategy: this.overlay.scrollStrategies.noop(),
        width: this.inputContainer.nativeElement.offsetWidth,
      }),
    );
  }

  private detachDropdownOverlay(): void {
    this.documentScrollEvent?.();
    this.documentScrollEvent = undefined;
    this.dropdownOverlayRef?.detach();
  }

  ngOnDestroy(): void {
    this.documentScrollEvent?.();
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }

    if (this.valueChangedSubscription) {
      this.valueChangedSubscription.unsubscribe();
    }

    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
    this.dropdownOverlayRef?.dispose();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('options' in changes) {
      this.getSelectedItems();
    }

    if ('invalidInputs' in changes) {
      this.getSelectedItems();
    }

    if ('secondaryPlaceholder' in changes) {
      // optional chaining is required as the initial onChanges can run
      // before #rawInput viewChild is ready
      this.inputWidth =
        (this.rawInput?.nativeElement.value.length ?? 0) +
        this.secondaryPlaceholder.length +
        1;
    }

    if ('control' in changes || 'dynamicValidation' in changes) {
      if (this.errorSubscription) {
        this.errorSubscription.unsubscribe();
      }
      this.errorSubscription = this.control.statusChanges.subscribe(() => {
        if (this.hasError && !this.isWarning && this.dynamicValidation) {
          this.shakeState = 'active';
        }
        this.changeDetectorRef.markForCheck();
      });

      // This sets the control's updateOn value to `blur` if on dynamicValidation
      if (this.control && this.dynamicValidation) {
        this.control = Object.defineProperty(this.control, 'updateOn', {
          get: () => 'blur',
          configurable: true,
        });
      }

      this.getSelectedItems();
    }

    if (
      'maxSelections' in changes ||
      'counterPosition' in changes ||
      'hasError' in changes
    ) {
      this.computeCounterTextClass();
    }

    if ('textColor' in changes || 'showPlusIcon' in changes) {
      this.computeDropdownItemTextClasses();
    }

    if ('disableDropdown' in changes && this.disableDropdown) {
      this.detachDropdownOverlay();
    }
  }

  showDropdown(): void {
    this.dropdownVisible = true;
    // cancel closing the dropdown if we were about to
    if (this.dropdownHideTimeout) {
      clearTimeout(this.dropdownHideTimeout);
      this.dropdownHideTimeout = undefined;
    }
  }

  handleTouchStart(index: number): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }

    this.longPressTimeout = this.timeUtils.setTimeout(() => {
      this.modifyItem(index);
    }, 500);
  }

  handleTouchEnd(): void {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
  }

  handleInputFocus(): void {
    this.isFocused = true;
    this.highlightIndex = 0;
    this.refilterOptions();
    if (this.openDropdownOnFocus) {
      this.showDropdown();
    }

    if (this.dynamicValidation && this.hasError) {
      this.isWarning = true;
    }

    this.onFocus.emit();
  }

  /** Programmatically focus the raw input element. */
  focusInput(): void {
    if (this.rawInput) {
      this.focus.focusElement(this.rawInput);
    }
  }

  handleInputBlur(): void {
    this.isFocused = false;
    // hide dropdown after a timeout so clicks still register
    this.dropdownHideTimeout = this.timeUtils.setTimeout(() => {
      if (this.blurBehaviour === 'add') {
        if (this.allowBulkEntry) {
          const bulkEntries = parseEmailCSVValues(
            this.bulkEntrySeparators,
            this.inputControl.value,
          );

          if (bulkEntries.length > 1) {
            bulkEntries.forEach(entry =>
              this.addItem({
                item: { displayText: entry, value: entry },
                validateOnAdd: true,
              }),
            );
          } else {
            this.addItem({ validateOnAdd: true });
          }
        } else {
          this.addItem({ validateOnAdd: true });
        }
      }
      this.highlightIndex = 0;
      this.dropdownVisible = false;
      this.showHighlight = this.showHighlightInitially;
      this.changeDetectorRef.markForCheck();
    }, 250);

    if (this.dynamicValidation) {
      this.getSelectedItems();
      this.validateField();
      this.isWarning = false;
    }

    if (this.hasError) {
      this.shakeState = 'active';
    }
    this.onBlur.emit();
    this.changeDetectorRef.markForCheck();

    switch (this.blurBehaviour) {
      case 'clear':
        this.rawInput.nativeElement.value = '';
        break;
      case 'add':
        // No-op - this has been handled in the timeout
        break;
      case 'none':
        break;
      default:
        throw new Error(`Unknown blurBehaviour: ${this.blurBehaviour}`);
    }
  }

  handleInputKeyDown(e: KeyboardEvent): void {
    // eslint-disable-next-line deprecation/deprecation
    const k = (e.key && e.key.toLowerCase()) || e.keyCode;
    switch (k) {
      case 'backspace':
      case 8: {
        if (this.rawInput.nativeElement.value === '') {
          this.removeItem();
        }
        break;
      }
      /**
       * 'down' is an IE/Edge specific value
       * 40 is a fallback if e.key is undefined
       */
      case 'arrowdown':
      case 'down':
      case 40: {
        // Prevent cursor from moving to the right
        e.preventDefault();
        break;
      }
      /**
       * 'up' is an IE/Edge specific value
       * 38 is a fallback if e.key is undefined
       */
      case 'arrowup':
      case 'up':
      case 38: {
        // Prevent cursor from moving to the right
        e.preventDefault();
        break;
      }
      default:
        break; // Do nothing
    }
    this.inputWidth =
      this.rawInput.nativeElement.value.length +
      this.secondaryPlaceholder.length +
      1;
  }

  handleInputKeyUp(e: KeyboardEvent): false | undefined {
    // eslint-disable-next-line deprecation/deprecation
    const k = (e.key && e.key.toLowerCase()) || e.keyCode;
    // On the second letter onwards make sure dropdown is visible
    if (this.rawInput.nativeElement.value.length >= 1) {
      this.showDropdown();
    }
    // Hide it on escape and delete input value
    switch (k) {
      case 'escape':
      case 27: {
        this.rawInput.nativeElement.value = '';
        this.highlightIndex = 0;
        this.dropdownVisible = false;
        this.showHighlight = this.showHighlightInitially;
        break;
      }
      case 'enter':
      case 13: {
        if (this.dropdownVisible) {
          e.stopPropagation();
        }

        this.refilterOptions();

        if (this.allowBulkEntry) {
          const bulkEntries = parseEmailCSVValues(
            this.bulkEntrySeparators,
            this.inputControl.value,
          );

          if (bulkEntries.length > 1) {
            bulkEntries.forEach(entry =>
              this.addItem({ item: { displayText: entry, value: entry } }),
            );
          } else {
            this.addItem();
          }
        } else {
          this.addItem();
        }

        break;
      }
      /**
       * 'down' is an IE/Edge specific value
       * 40 is a fallback if e.key is undefined
       */
      case 'arrowdown':
      case 'down':
      case 40: {
        if (!this.dropdownVisible || !this.showHighlight) {
          this.showHighlight = true;
          this.showDropdown();
          this.moveUp();
        }
        this.moveDown();
        e.stopPropagation();
        return false;
      }
      /**
       * 'up' is an IE/Edge specific value
       * 38 is a fallback if e.key is undefined
       */
      case 'arrowup':
      case 'up':
      case 38: {
        if (!this.showHighlight) {
          this.showHighlight = true;
        }

        this.moveUp();
        e.stopPropagation();
        return false;
      }
      default:
        break; // Do nothing
    }
  }

  fixupHighlightOverflow(): void {
    if (this.highlightIndex < 0) {
      this.highlightIndex += this.filteredOptions.length;
    } else if (this.highlightIndex >= this.filteredOptions.length) {
      this.highlightIndex = 0;
    }
  }

  checkForDuplicate(inputValue: string): boolean {
    if (this.control.value) {
      const currentItems = this.control.value.map(controlValue =>
        typeof controlValue === 'string'
          ? controlValue.toLowerCase()
          : controlValue,
      );

      return currentItems.indexOf(inputValue.trim().toLowerCase()) > -1;
    }
    return false;
  }

  addItem({
    item,
    validateOnAdd = false,
  }: {
    item?: SelectItem<string | T>;
    validateOnAdd?: boolean;
  } = {}): void {
    let itemToAdd: SelectItem<string | T> | undefined;
    if (item) {
      // user clicked on an item directly
      itemToAdd = item;
    } else if (this.getItem()) {
      // input is blurred or user pressed enter with an item highlighted
      itemToAdd = this.getItem();
    }

    if (!itemToAdd || itemToAdd.disabled) {
      return;
    }

    const previousValueHasError = this.hasError;
    const previousSelections = this.control.value ?? [];
    const newSelections = this.control.value
      ? [...this.control.value, itemToAdd.value]
      : [itemToAdd.value];

    if (
      this.isAllowCustomEntryControl(this.control) &&
      this.isAllowCustomEntryItems(newSelections)
    ) {
      this.control.setValue(newSelections, { emitEvent: false });
    } else if (this.isNotAllowCustomEntryItems(newSelections)) {
      this.control.setValue(newSelections, { emitEvent: false });
    }

    // Remove item if it is invalid
    if (validateOnAdd && this.control.status === 'INVALID') {
      this.control.setValue(previousSelections, { emitEvent: false });
    }

    this.getSelectedItems();

    if (this.dynamicValidation) {
      this.handleDynamicValidation(previousValueHasError);
    } else {
      dirtyAndValidate(this.control);
    }

    this.highlightIndex = 0;
    this.inputControl.setValue('');
    this.refilterOptions();
    this.dropdownVisible = false;
    this.showHighlight = this.showHighlightInitially;
    this.focus.focusElement(this.rawInput);
    this.scrollInputIntoView();
  }

  modifyItem(index: number): void {
    const item = this.selectedItems[index];
    if (item?.displayText) {
      this.removeItem(index);
      this.inputControl.setValue(item.displayText);
      this.dropdownVisible = true;
    }
  }

  removeItem(index?: number): void {
    const newSelections: (string | T)[] = [...this.control.value];
    const removeIndex = index ?? newSelections.length - 1;
    const [removedItem] =
      removeIndex >= 0 && removeIndex < newSelections.length
        ? newSelections.splice(removeIndex, 1)
        : [];

    if (removedItem) {
      this.itemRemoved.emit(removedItem);
    }

    const previousValueHasError = this.hasError;
    if (
      this.isAllowCustomEntryControl(this.control) &&
      this.isAllowCustomEntryItems(newSelections)
    ) {
      this.control.setValue(newSelections, { emitEvent: false });
    } else if (this.isNotAllowCustomEntryItems(newSelections)) {
      this.control.setValue(newSelections, { emitEvent: false });
    }
    this.getSelectedItems();

    if (this.dynamicValidation) {
      this.handleDynamicValidation(previousValueHasError);
    } else {
      dirtyAndValidate(this.control);
    }

    this.refilterOptions();
    this.dropdownVisible = false;
    this.showHighlight = this.showHighlightInitially;
    this.focus.focusElement(this.rawInput);
  }

  /**
   * Scrolls the raw input element into view, used after adding an item to ensure the input is visible
   * if we have enough items to cause overflow and scrolling.
   */
  scrollInputIntoView(): void {
    this.timeUtils.setTimeout(() => {
      this.rawInput.nativeElement.scrollIntoView({ block: 'nearest' });
    });
  }

  // Scrolls the dropdown menu
  scrollItemIntoView(): void {
    const elements = document.querySelectorAll('.DropdownItem');

    // if elements[this.highlightIndex] exist, scroll to it
    if (elements[this.highlightIndex]) {
      const e = elements[this.highlightIndex] as Element & {
        scrollIntoViewIfNeeded?(): void;
      };

      if (e.scrollIntoViewIfNeeded) {
        e.scrollIntoViewIfNeeded();
      } else {
        e.scrollIntoView();
      }
    }
  }

  // Gets the highlighted item in the dropdown menu
  getItem(): SelectItem<string | T> | undefined {
    return this.dropdownVisible
      ? this.filteredOptions[this.highlightIndex]
      : undefined;
  }

  handleMousedown(event: Event): void {
    // Prevents input blur from being called when clicking on the dropdown options
    // or when dismissing an option
    event.preventDefault();
  }

  handleDropdownClick(item: SelectItem<string | T>): void {
    if (this.allowBulkEntry) {
      const bulkEntries = parseEmailCSVValues(
        this.bulkEntrySeparators,
        String(item.value),
      );

      if (bulkEntries.length > 1) {
        bulkEntries.forEach(entry =>
          this.addItem({ item: { displayText: entry, value: entry } }),
        );
      } else {
        this.addItem({ item });
      }
    } else {
      this.addItem({ item });
    }
  }

  countSelectedItems(): void {
    if (this.maxSelections) {
      this.selectionsCount = this.control.value.length;

      // Recompute the counter text class to update color when selectionsCount changes
      this.computeCounterTextClass();
    }
  }

  // Sets the fl-tags based on the control to display on the input field
  getSelectedItems(): void {
    if (this.control && this.control.value) {
      this.selectedItems = this.control.value.map(value => {
        const found = this.options.find(item => isEqual(item.value, value));
        if (found) {
          return found;
        }

        const isInvalid = this.invalidInputs?.includes(String(value)) ?? false;
        let iconConfig: IconConfig | undefined;
        if (this.showContactIcons && value && typeof value === 'string') {
          const iconColor = isInvalid ? IconColor.ERROR : IconColor.FOREGROUND;
          const isEmail = value.includes('@') && !value.startsWith('@');
          const isPhone = validContactNumberRegex.test(value.trim());
          iconConfig = {
            name: isEmail ? 'ui-mail' : isPhone ? 'ui-phone' : 'ui-person',
            color: iconColor,
            size: IconSize.SMALL,
            useIconFont: true,
            fillIconFont: true,
          };
        }

        // Using `String()` as `toString` requires `T extends Object`, but using that causes
        // `T` to get inferred as `any`
        return {
          displayText: String(value),
          value,
          isInvalid,
          iconConfig,
        };
      });
      this.countSelectedItems();
    }
  }

  // Filters the dropdown choices based on the input field text
  refilterOptions(): void {
    const rawValue = this.rawInput.nativeElement.value.trim();
    const lowerCaseRawValue = rawValue.toLowerCase();
    this.showAddingLine = false;
    this.filteredOptions = this.options.filter(option => {
      if (
        // Check if control has values
        (this.control.value &&
          // `allowCustomEntry` - control and options should be all string
          // These type guards should all be true when `allowCustomEntry` is true
          this.isAllowCustomEntryControl(this.control) &&
          this.isAllowCustomEntrySelectItem(option) &&
          this.control.value.includes(option.value)) ||
        // No `allowCustomEntry` - control and options should be T
        // These type guards should all be true when `allowCustomEntry` is falsy
        (this.isNotAllowCustomEntrySelectItem(option) &&
          this.control.value.includes(option.value))
      ) {
        return false;
      }

      // Filter out options doesn't partially or fully match the raw user input
      if (!option.displayText.toLowerCase().includes(lowerCaseRawValue)) {
        return false;
      }

      return true;
    });
    // only show "Adding '{text}'" if filteredOptions has multiple elements
    // or empty or there is 1 filteredOptions but the displayText !== user's input
    if (
      this.allowCustomEntry &&
      rawValue !== '' &&
      (this.filteredOptions.length !== 1 ||
        (this.filteredOptions.length === 1 &&
          this.filteredOptions[0].displayText.toLowerCase() !==
            rawValue.toLowerCase()))
    ) {
      const duplicateDetected = this.checkForDuplicate(rawValue);
      const isAddable = this.isCustomEntryValid?.(rawValue) ?? true;

      if (!duplicateDetected) {
        this.showAddingLine = isAddable;
        this.filteredOptions.unshift({
          displayText: `${this.addingText} '${rawValue}'`,
          value: rawValue,
          disabled: !isAddable,
        });
      } else {
        this.filteredOptions.unshift({
          displayText: `'${rawValue}' is already added`,
          value: rawValue,
          disabled: true,
        });
      }
    }

    this.computeDropdownItemTextClasses();
    // The overlay view is OnPush; mark it so refilter triggers a repaint.
    this.changeDetectorRef.markForCheck();
  }

  // Move down the dropdown choices
  moveDown(): void {
    this.highlightIndex += 1;
    this.fixupHighlightOverflow();
    this.scrollItemIntoView();
  }

  // Move up the dropdown choices
  moveUp(): void {
    this.highlightIndex -= 1;
    this.fixupHighlightOverflow();
    this.scrollItemIntoView();
  }

  handleDynamicValidation(hasError: boolean): void {
    // We want to only keep validating if there is already an existing error
    if (!this.dynamicValidation || !hasError) {
      return;
    }

    this.validateField();
  }

  validateField(): void {
    if (this.selectedItems.length > 0) {
      dirtyAndValidate(this.control);
    } else {
      this.control.reset([]);
    }
  }

  trackBySelectItem(_: number, item: SelectItem<string | T>): string | T {
    return item.value;
  }

  shakeDone(): void {
    this.shakeState = 'void';
  }

  isAllowCustomEntryItems(items: (string | T)[]): items is string[] {
    return this.allowCustomEntry === true;
  }

  isNotAllowCustomEntryItems(items: (string | T)[]): items is T[] {
    return !this.allowCustomEntry;
  }

  isAllowCustomEntrySelectItem(
    item: SelectItem<string | T>,
  ): item is SelectItem<string> {
    return this.allowCustomEntry === true;
  }

  isNotAllowCustomEntrySelectItem(
    item: SelectItem<string | T>,
  ): item is SelectItem<T> {
    return !this.allowCustomEntry;
  }

  isAllowCustomEntryControl(
    control: AbstractControl,
  ): control is FormControl<string[]> {
    return this.allowCustomEntry === true;
  }

  private generateUniqueID(): string {
    return Math.random().toString(36).substring(2, 6);
  }

  private computeDropdownItemTextClasses(): void {
    // Only the adding line differs, so resolve both variants once - calling
    // `textVariants` per option costs O(options) every time the filter widens.
    const regular = this.buildDropdownItemTextClasses('DropdownItemText');
    const addingLine =
      this.showAddingLine && this.showPlusIcon
        ? this.buildDropdownItemTextClasses('DropdownItemText IsBlueText')
        : regular;

    this.dropdownItemTextClasses = this.filteredOptions.map((_, index) =>
      index === 0 ? addingLine : regular,
    );
  }

  private buildDropdownItemTextClasses(extraClass: string): {
    readonly normal: string;
    readonly highlighted: string;
  } {
    return {
      normal: textVariants({ color: this.textColor, class: extraClass }),
      highlighted: textVariants({ color: FontColor.LIGHT, class: extraClass }),
    };
  }

  private computeCounterTextClass(): void {
    const color =
      this.selectionsCount <= (this.maxSelections ?? Number.MAX_SAFE_INTEGER)
        ? this.hasError &&
          this.counterPosition === MultiSelectCounterPosition.RIGHT
          ? FontColor.ERROR
          : FontColor.FOREGROUND
        : FontColor.ERROR;

    this.counterTextCls = textVariants({ color });
  }
}
